import CONFIG from "@APP/config";

const ALLOWED_PROTOCOLS = ["http:", "https:"];

/**
 * Origins we are willing to navigate the browser to. Invoice/attachment URLs are
 * produced by our own backend, so they resolve either to the app itself (the API is
 * proxied under `/api`) or to the configured API root for the current environment.
 */
const getAllowedOrigins = (): string[] => {
  const origins = [window.location.origin];

  try {
    origins.push(new URL(CONFIG.API.ROOT_URL).origin);
  } catch {
    // A malformed ROOT_URL simply contributes no extra allowed origin.
  }

  return origins;
};

/**
 * Validates a URL that came from a remote resource before it is used for navigation.
 * Returns the normalised absolute URL when it is safe to use, otherwise `null`.
 *
 * Guards against open redirects and script-scheme injection (`javascript:`, `data:`)
 * by allowing only http(s) URLs on an allow-listed origin.
 */
export const sanitizeNavigationUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  let parsed: URL;

  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) return null;
  if (!getAllowedOrigins().includes(parsed.origin)) return null;

  return parsed.toString();
};

/**
 * Saves a blob to disk via a temporary anchor.
 *
 * Downloading rather than navigating to the blob URL keeps remotely fetched content out
 * of the browsing context: a `blob:` document inherits the origin of the page that
 * created it, so navigating to a blob of an unexpected media type could execute
 * attacker-controlled markup as if it came from us.
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  link.rel = "noopener";

  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoke once the browser has had a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};
